import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { api, query } from '../lib/api/client';
import type { PaginaDeItens } from '../lib/api/types';
import { useAuth } from '../lib/auth/AuthContext';
import { useMarcaAtiva } from '../lib/auth/MarcaAtiva';
import { PageHeader } from '../components/ui/PageHeader';
import { Carregando, Erro, Vazio } from '../components/ui/Estados';
import { emDataHora } from '../lib/format/datetime';

/**
 * A busca de itens (A13) — a porta de entrada real do painel.
 *
 * <h3>O filtro mora na URL</h3>
 * Uma busca que não pode ser colada num chamado é uma busca que vira captura de tela. Com o
 * filtro no endereço, "olha este aqui" é um link, o botão voltar funciona, e duas pessoas
 * conseguem estar olhando comprovadamente a mesma lista.
 *
 * <h3>A marcação vem na linha, e não numa consulta por item</h3>
 * É o motivo pelo qual um item aparece em vermelho. Buscá-la por linha seria N+1 na tela
 * mais usada do produto — e a tela mais usada é onde a lentidão vira "o painel é ruim".
 */
export function ItensPage() {
  const { traduzir, vocabulario } = useAuth();
  const { marcaAtiva } = useMarcaAtiva();
  const [params, setParams] = useSearchParams();

  const serial = params.get('serial') ?? '';
  const posicao = params.get('position') ?? '';
  const marcacao = params.get('marking') ?? '';
  const lacreAberto = params.get('sealOpen') === 'true';
  const pagina = Number(params.get('page') ?? '0');

  const [digitado, setDigitado] = useState(serial);

  const busca = useQuery({
    queryKey: ['itens', serial, posicao, marcacao, lacreAberto, pagina, marcaAtiva],
    queryFn: () =>
      api.get<PaginaDeItens>(
        `/console/items${query({
          serial,
          position: posicao,
          marking: marcacao,
          sealOpen: lacreAberto ? 'true' : undefined,
          brandId: marcaAtiva,
          page: pagina,
          size: 25,
        })}`,
      ),
  });

  function trocar(chave: string, valor: string) {
    const novo = new URLSearchParams(params);
    if (valor) {
      novo.set(chave, valor);
    } else {
      novo.delete(chave);
    }
    // Trocar filtro sem voltar à primeira página deixa a pessoa numa página que não existe
    // mais, e a tela responde vazia — com cara de "não encontrei nada".
    novo.delete('page');
    setParams(novo);
  }

  return (
    <>
      <PageHeader
        titulo="Itens"
        subtitulo="Busque pelo código do produto, ou filtre por situação e marcação."
      />
      <div className="wrap">
        <form
          className="bigsearch"
          onSubmit={(e) => {
            e.preventDefault();
            trocar('serial', digitado.trim());
          }}
        >
          <input
            className="txt"
            style={{ border: 0, outline: 'none', background: 'transparent' }}
            placeholder="Código do item, ou parte dele"
            value={digitado}
            onChange={(e) => setDigitado(e.target.value)}
          />
          <button className="btn btn-primary" type="submit">
            Buscar
          </button>
        </form>

        <div className="filtros" style={{ marginTop: 18 }}>
          <div className="campo">
            <label htmlFor="pos">Situação</label>
            <select id="pos" value={posicao} onChange={(e) => trocar('position', e.target.value)}>
              <option value="">Todas</option>
              {Object.entries(vocabulario.position ?? {}).map(([codigo, texto]) => (
                <option key={codigo} value={codigo}>
                  {texto}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <label htmlFor="marc">Marcação</label>
            <select id="marc" value={marcacao} onChange={(e) => trocar('marking', e.target.value)}>
              <option value="">Qualquer</option>
              {Object.entries(vocabulario.marking ?? {}).map(([codigo, texto]) => (
                <option key={codigo} value={codigo}>
                  {texto}
                </option>
              ))}
            </select>
          </div>
          <label className="campo" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              style={{ width: 16, height: 16, margin: 0 }}
              checked={lacreAberto}
              onChange={(e) => trocar('sealOpen', e.target.checked ? 'true' : '')}
            />
            Só lacre aberto
          </label>
          {(serial || posicao || marcacao || lacreAberto) && (
            <button
              className="btn btn-ghost"
              onClick={() => {
                setDigitado('');
                setParams(new URLSearchParams());
              }}
            >
              Limpar filtros
            </button>
          )}
        </div>

        {busca.isPending && <Carregando o="os itens" />}
        {busca.isError && <Erro erro={busca.error} />}
        {busca.data && busca.data.conteudo.length === 0 && (
          <Vazio
            titulo="Nenhum item com esses filtros"
            detalhe="Confira o código digitado, ou limpe os filtros para ver o lote inteiro."
          />
        )}

        {busca.data && busca.data.conteudo.length > 0 && (
          <div className="card tbl">
            <table className="t">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Produto</th>
                  <th>Lote</th>
                  <th>Situação</th>
                  <th>Marcações</th>
                  <th>Garantia</th>
                  <th>Lacre</th>
                  <th>Última leitura</th>
                </tr>
              </thead>
              <tbody>
                {busca.data.conteudo.map((i) => (
                  <tr key={i.id}>
                    <td>
                      <Link to={`/itens/${i.id}`} className="mono-s" style={{ fontWeight: 600 }}>
                        {i.serial}
                      </Link>
                    </td>
                    <td>{i.produto}</td>
                    <td className="t-cap">{i.lote}</td>
                    <td>
                      <span className="chip chip-neutral">{traduzir('position', i.posicao)}</span>
                    </td>
                    <td>
                      {i.marcacoes.length === 0 ? (
                        <span className="dash">—</span>
                      ) : (
                        i.marcacoes.map((m) => (
                          <span key={m} className="chip chip-danger" style={{ marginRight: 4 }}>
                            {traduzir('marking', m)}
                          </span>
                        ))
                      )}
                    </td>
                    <td>
                      <span className={i.nivel === 'L2' ? 'chip chip-teal' : 'chip chip-neutral'}>
                        {i.nivel}
                      </span>
                    </td>
                    <td>
                      {/* Da última leitura verificada (ADR-123): o estado vale por estar no CMAC. */}
                      {i.lacre ? (
                        <span className={i.lacre === 'OPEN' ? 'chip chip-danger' : 'chip chip-neutral'}>
                          {traduzir('seal', i.lacre)}
                        </span>
                      ) : (
                        <span className="dash">—</span>
                      )}
                    </td>
                    <td className="t-cap">{emDataHora(i.ultimoEventoEm)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="pager">
              <span>
                {busca.data.total} item(ns) · página {busca.data.pagina + 1}
              </span>
              <div className="sp" style={{ flex: 1 }} />
              <button
                className="btn btn-secondary"
                disabled={pagina === 0}
                onClick={() => trocarPagina(pagina - 1)}
              >
                Anterior
              </button>
              <button
                className="btn btn-secondary"
                disabled={(pagina + 1) * busca.data.tamanho >= busca.data.total}
                onClick={() => trocarPagina(pagina + 1)}
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );

  function trocarPagina(nova: number) {
    const novo = new URLSearchParams(params);
    novo.set('page', String(Math.max(nova, 0)));
    setParams(novo);
  }
}
